import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageContainer, Section } from '../../components/AdminContentWrapper';
import RequireBillingRole from './components/RequireBillingRole';
import ConsumptionBucketsTable from './components/ConsumptionBucketsTable';
import CreditsEngineService from '../../../../services/CreditsEngineService';
import HelpIcon from '../../../../components/help/HelpIcon';

const darkTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--wb-text-strong)',
    '& fieldset': { borderColor: 'var(--wb-outline)' },
    '&:hover fieldset': { borderColor: 'var(--wb-text-dim)' }
  },
  '& .MuiInputLabel-root': { color: 'var(--wb-text-soft)' }
};

const BillingGlobalConsumptionContent = () => {
  const [period, setPeriod] = useState('day');
  const [accountIdFilter, setAccountIdFilter] = useState('');
  const [buckets, setBuckets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await CreditsEngineService.getAdminUsageBuckets({
        bucket: period,
        accountId: accountIdFilter.trim() || undefined
      });
      setBuckets(response || []);
    } catch (err) {
      setError(err.message || 'No fue posible obtener el consumo global.');
    } finally {
      setIsLoading(false);
    }
  }, [period, accountIdFilter]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  return (
    <PageContainer
      title="Consumo global"
      description="Consumo de créditos/tokens de TODOS los tenants, agregado por hora, día o mes. Filtra por account_id para ver un tenant específico."
      actions={
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={isLoading}
          sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}>
          Actualizar
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      <Section title="Filtros">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel sx={{ color: 'var(--wb-text-soft)' }}>Período</InputLabel>
              <Select value={period} label="Período" onChange={(e) => setPeriod(e.target.value)} sx={{ color: 'var(--wb-text-strong)' }}>
                <MenuItem value="hour">Por hora</MenuItem>
                <MenuItem value="day">Por día</MenuItem>
                <MenuItem value="month">Por mes</MenuItem>
              </Select>
            </FormControl>
            <HelpIcon module="creditos" topic="consumo-global" anchor="período" />
          </Stack>
          <TextField
            label="Filtrar por account_id (opcional)"
            value={accountIdFilter}
            onChange={(e) => setAccountIdFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            size="small"
            sx={{ flex: 1, ...darkTextFieldStyle }}
            InputProps={{ endAdornment: (
              <InputAdornment position="end"><HelpIcon module="creditos" topic="consumo-global" anchor="filtrar-por-account_id-opcional" /></InputAdornment>
            ) }}
          />
          <Button variant="contained" onClick={load} sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
            Buscar
          </Button>
        </Stack>
      </Section>

      <Section title="Consumo por tenant" subtitle="Scan con filtro (endpoint de bajo tráfico) — puede tardar un poco con muchos tenants activos.">
        <ConsumptionBucketsTable buckets={buckets} isLoading={isLoading} showScope />
      </Section>
    </PageContainer>
  );
};

const BillingGlobalConsumption = () => (
  <RequireBillingRole allow="root" title="Consumo global">
    <BillingGlobalConsumptionContent />
  </RequireBillingRole>
);

export default BillingGlobalConsumption;
